import { EditOutlined, EyeFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Tooltip } from 'antd';
import moment from 'moment';
import { ArrowClockwise, ChatText, PencilSimpleLine } from 'phosphor-react';
import React, { useCallback, useMemo, useState } from 'react';
import podcastPlaceholder from '../../assets/images/imagePlaceholder.png';
import {
  CLOUD_FRONT_CACHE_KEYS,
  MODULES,
  PAGE_TYPES,
  ROUTES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { getImageUrl } from '../../common/utils';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import Image from '../../components/Image';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import CustomCard from '../component/card/Cscard';
import { INVALIDATE_CLOUD_FRONT_CACHE } from '../menus/graphql/Mutations';
import QuestionModal from './components/QuestionModal';
import { GET_PODCASTS_MODULE } from './graphql/Queries';

const PodcastCard = ({
  createdAt,
  id,
  title,
  description,
  imageThumbnail,
  onEdit,
  onPreview,
  isViewOnly,
  isAssetAllowed,
  isLabelAllowed
}) => {
  const [open, setOpen] = useState(false);
  const handleQuestion = () => {
    setOpen(true);
  };

  return (
    <>
      {open && <QuestionModal open={open} setOpen={setOpen} questionId={id} />}
      <CustomCard
        onClick={onPreview}
        alt={title}
        src={getImageUrl(imageThumbnail?.url) ?? podcastPlaceholder}
        blurHash={imageThumbnail?.blurhash}
        title={moment(createdAt).format('ll')}
        heading={title}
        description={description}
        primaryOnClick={() => onEdit(id)}
        primary={
          isAssetAllowed &&
          isLabelAllowed &&
          (isViewOnly ? (
            <>
              <EyeFilled size={16} />
              View
            </>
          ) : (
            <>
              <PencilSimpleLine size={16} />
              Edit
            </>
          ))
        }
        handleQuestion={handleQuestion}
        instanceType={PAGE_TYPES.PODCAST}
      />
    </>
  );
};

function ViewQuestionAction({ id }) {
  const [open, setOpen] = useState(false);
  const handleQuestion = () => {
    setOpen(true);
  };

  return (
    <>
      {open && <QuestionModal open={open} setOpen={setOpen} questionId={id} />}
      <div className="d-flex align-center">
        <Tooltip title="Manage Question">
          <Button type="text" className="text-btn" onClick={handleQuestion}>
            <ChatText width={24} height={24} />
          </Button>
        </Tooltip>
      </div>
    </>
  );
}

const getColumns = ({
  handleEdit: onEdit,
  isAssetAllowed,
  isLabelAllowed,
  isViewOnly
}) => [
  {
    title: 'Video',
    dataIndex: 'imageThumbnail',
    ellipsis: true,
    render: (value, { title }) => (
      <Image
        className="group-img-table"
        blurHash={value?.blurhash}
        src={
          getImageUrl(value?.url, { height: 50, width: 80 }) ??
          podcastPlaceholder
        }
        alt={title}
      />
    )
  },
  {
    title: 'Title',
    dataIndex: 'title',
    ellipsis: true
  },
  {
    title: 'Description',
    dataIndex: 'description',
    ellipsis: true
  },
  ...(isAssetAllowed && isLabelAllowed
    ? [
        {
          title: 'Action',
          dataIndex: 'id',
          render: (value) => (
            <div className="d-flex align-center">
              <ViewQuestionAction id={value} />
              <Tooltip title={`${isViewOnly ? 'View' : 'Edit'} Podcast`}>
                <Button
                  type="text"
                  className="text-btn"
                  onClick={() => onEdit(value)}
                >
                  {isViewOnly ? <EyeFilled /> : <EditOutlined />}
                </Button>
              </Tooltip>
            </div>
          )
        }
      ]
    : [])
];

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder
}) => ({
  filter: {
    skip: offset,
    search,
    limit
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ podcastsAdmin }) => ({
  data: podcastsAdmin?.podcasts ?? [],
  count: podcastsAdmin?.count ?? 0
});

const Podcast = ({ history }) => {
  const filterProps = useFilterBar();
  const [invalidateCache, { loading: isInvalidating }] = useMutation(
    INVALIDATE_CLOUD_FRONT_CACHE,
    {
      variables: { key: CLOUD_FRONT_CACHE_KEYS.OG_IMAGES }
    }
  );
  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isLabelAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.LABEL_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isAssetAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const handleShowModal = () => {
    history.push(`${ROUTES?.PODCAST_MODULE}/add`);
  };

  const handleEdit = useCallback((id) => {
    history.push(`${ROUTES?.PODCAST_MODULE}/${id}/edit`);
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        handleEdit,
        isAddEditAllowed,
        isAssetAllowed,
        isLabelAllowed,
        isViewOnly
      }),
    [handleEdit]
  );

  return (
    <div>
      <PageHeader
        menu={MODULES?.CONTENT_UNITS}
        handleShowModal={handleShowModal}
        isAddEditAllowed={isAddEditAllowed && isAssetAllowed && isLabelAllowed}
      />
      <FilterBar
        {...filterProps}
        SearchProps={{ placeholder: 'Search Podcasts' }}
      >
        <Tooltip title="Revalidate OG images" placement="left">
          <Button
            className="text-btn d-flex align-center justify-center"
            type="text"
            icon={<ArrowClockwise size={24} />}
            onClick={invalidateCache}
            loading={isInvalidating}
          />
        </Tooltip>
      </FilterBar>
      <PageList
        filters={filterProps.filters}
        listMode={filterProps.listMode}
        query={GET_PODCASTS_MODULE}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        GridProps={{
          renderItem: (props) => (
            <PodcastCard
              {...props}
              onEdit={handleEdit}
              isAddEditAllowed={isAddEditAllowed}
              isAssetAllowed={isAssetAllowed}
              isLabelAllowed={isLabelAllowed}
              isViewOnly={isViewOnly}
            />
          )
        }}
        TableProps={{ columns }}
      />
    </div>
  );
};

export default Podcast;
